import { useState, useEffect } from 'react'
import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import {
  LayoutDashboard,
  Star,
  FileText,
  MessageSquare,
  Bell,
  Settings,
  LogOut,
  Menu,
  X,
  Upload,
  TrendingUp,
  Award,
  Search,
  Trophy
} from 'lucide-react'
import supabase from '../../services/supabaseClient'
import LanguageSelector from '../../components/LanguageSelector'

export default function StudentDashboard() {
  const { t } = useTranslation()
  const { profile, signOut } = useAuth()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [myPoints, setMyPoints] = useState(0)
  const [myPortfolio, setMyPortfolio] = useState([])
  const [stats, setStats] = useState({
    totalPoints: 0,
    portfolioItems: 0,
    unreadMessages: 0
  })

  useEffect(() => {
    if (profile?.id) {
      fetchStudentData()
    }
  }, [profile])

  async function fetchStudentData() {
    try {
      const { data: studentRecord, error: studentError } = await supabase
        .from('learners')
        .select('id')
        .eq('email', profile.email)
        .single()

      if (studentError || !studentRecord) {
        console.log('Student record not found')
        return
      }

      const learnerId = studentRecord.id

      const { data: points } = await supabase
        .from('points')
        .select('points')
        .eq('learner_id', learnerId)

      const totalPoints = points?.reduce((sum, p) => sum + p.points, 0) || 0

      const { data: portfolio, error: portfolioError } = await supabase
        .from('portfolios')
        .select('*')
        .eq('learner_id', learnerId)
        .order('created_at', { ascending: false })
        .limit(5)

      if (portfolioError) throw portfolioError

      const { count: messagesCount } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_id', profile.id)
        .eq('is_read', false)

      setStats({
        totalPoints,
        portfolioItems: portfolio?.length || 0,
        unreadMessages: messagesCount || 0
      })
      setMyPortfolio(portfolio || [])
    } catch (error) {
      console.error('Error fetching student data:', error)
    }
  }

  const navigation = [
    { name: t('common.dashboard'), href: '/student', icon: LayoutDashboard },
    { name: t('dashboard.myProgress'), href: '/student/progress', icon: TrendingUp },
    { name: t('dashboard.myPortfolio'), href: '/student/portfolio', icon: FileText },
    { name: t('common.messages'), href: '/student/messages', icon: MessageSquare },
    { name: t('common.announcements'), href: '/student/announcements', icon: Bell },
    { name: t('common.settings'), href: '/student/settings', icon: Settings },
  ]

  const isActive = (href) => location.pathname === href

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-72 flex-col bg-white border-r border-gray-100">
          <div className="flex h-20 items-center justify-between px-6 border-b border-gray-100">
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-xl gradient-primary flex items-center justify-center text-white font-bold shadow-glow">
                S
              </div>
              <span className="ml-3 text-lg font-bold text-text-primary">{t('roles.student')} {t('navigation.portal')}</span>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="p-2 rounded-lg hover:bg-gray-100 text-text-secondary">
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`nav-link ${isActive(item.href) ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className={`mr-3 h-5 w-5 ${isActive(item.href) ? 'text-primary-500' : 'text-gray-400'}`} />
                {item.name}
              </Link>
            ))}
          </nav>
          <div className="p-4 border-t border-gray-100">
            <button
              onClick={signOut}
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-text-secondary rounded-xl hover:bg-gray-100 hover:text-text-primary transition-colors"
            >
              <LogOut className="mr-3 h-5 w-5 text-gray-400" />
              {t('auth.signOut')}
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
        <div className="flex flex-col flex-1 min-h-0 bg-white border-r border-gray-100">
          <div className="flex h-20 items-center px-6 border-b border-gray-100">
            <div className="h-10 w-10 rounded-xl gradient-primary flex items-center justify-center text-white font-bold shadow-glow">
              S
            </div>
            <span className="ml-3 text-lg font-bold text-text-primary">{t('roles.student')} {t('navigation.portal')}</span>
          </div>
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`nav-link ${isActive(item.href) ? 'active' : ''}`}
              >
                <item.icon className={`mr-3 h-5 w-5 ${isActive(item.href) ? 'text-primary-500' : 'text-gray-400'}`} />
                {item.name}
              </Link>
            ))}
          </nav>
          <div className="p-4 border-t border-gray-100">
            <button
              onClick={signOut}
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-text-secondary rounded-xl hover:bg-gray-100 hover:text-text-primary transition-colors"
            >
              <LogOut className="mr-3 h-5 w-5 text-gray-400" />
              {t('auth.signOut')}
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Top header */}
        <header className="dashboard-header sticky top-0 z-30">
          <div className="flex h-20 items-center justify-between px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg text-text-secondary hover:bg-gray-100"
            >
              <Menu className="h-6 w-6" />
            </button>
            
            {/* Search */}
            <div className="hidden md:flex flex-1 max-w-md ml-8">
              <div className="relative w-full">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('common.search')}
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-white border border-gray-200 text-text-primary placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100 transition-all"
                />
              </div>
            </div>
            
            <div className="flex items-center ml-auto space-x-4">
              <LanguageSelector size="small" />
              <div className="flex items-center px-4 py-2 bg-card-green rounded-xl border border-success-200">
                <Trophy className="h-4 w-4 text-success-500 mr-2" />
                <span className="text-sm font-bold text-success-600">{stats.totalPoints} {t('dashboard.points')}</span>
              </div>
              <button className="p-3 rounded-xl text-text-secondary hover:text-text-primary hover:bg-gray-100 transition-colors">
                <Bell className="h-5 w-5" />
              </button>
              <button className="p-3 rounded-xl text-text-secondary hover:text-text-primary hover:bg-gray-100 transition-colors relative">
                <MessageSquare className="h-5 w-5" />
                {stats.unreadMessages > 0 && (
                  <span className="absolute top-1.5 right-1.5 h-5 w-5 bg-primary-500 rounded-full text-xs text-white flex items-center justify-center border-2 border-white">
                    {stats.unreadMessages}
                  </span>
                )}
              </button>
              <div className="flex items-center pl-4 border-l border-gray-100">
                <div className="h-10 w-10 rounded-xl gradient-primary flex items-center justify-center text-white font-semibold shadow-glow">
                  {profile?.first_name?.[0]}{profile?.last_name?.[0]}
                </div>
                <div className="ml-3 hidden sm:block">
                  <p className="text-sm font-semibold text-text-primary">{profile?.first_name}</p>
                  <p className="text-xs text-text-secondary">{t('roles.student')}</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="py-8 px-4 sm:px-6 lg:px-8">
          <Routes>
            <Route
              path="/"
              element={
                <div className="space-y-8 animate-fade-in">
                  {/* Page Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <h1 className="text-2xl font-bold text-text-primary font-heading">{t('common.my')} {t('common.dashboard')}</h1>
                      <p className="mt-1 text-sm text-text-secondary">
                        {t('dashboard.welcome', { name: profile?.first_name })}
                      </p>
                    </div>
                  </div>
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    <div className="stat-card hover-lift">
                      <div className="flex items-center">
                        <div className="stat-card-icon success">
                          <Star className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-text-secondary">{t('dashboard.myPoints')}</p>
                          <p className="text-2xl font-bold text-text-primary">{stats.totalPoints}</p>
                        </div>
                      </div>
                    </div>

                    <div className="stat-card hover-lift">
                      <div className="flex items-center">
                        <div className="stat-card-icon primary">
                          <FileText className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-text-secondary">{t('dashboard.portfolioItems')}</p>
                          <p className="text-2xl font-bold text-text-primary">{stats.portfolioItems}</p>
                        </div>
                      </div>
                    </div>

                    <div className="stat-card hover-lift">
                      <div className="flex items-center">
                        <div className="stat-card-icon warning">
                          <MessageSquare className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-text-secondary">{t('dashboard.unreadMessages')}</p>
                          <p className="text-2xl font-bold text-text-primary">{stats.unreadMessages}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* My Portfolio Preview */}
                  <div className="card p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-lg font-bold text-text-primary font-heading">{t('dashboard.myPortfolio')}</h2>
                      <div className="flex items-center space-x-3">
                        <Link
                          to="/student/portfolio/upload"
                          className="flex items-center px-4 py-2 text-sm font-semibold text-primary-600 bg-card-teal rounded-xl border border-primary-200 hover:bg-primary-50 transition-colors"
                        >
                          <Upload className="h-4 w-4 mr-1.5" />
                          {t('common.upload')}
                        </Link>
                        <Link to="/student/portfolio" className="text-sm font-semibold text-primary-600 hover:text-primary-700">
                          {t('dashboard.viewAll')}
                        </Link>
                      </div>
                    </div>
                    {myPortfolio.length === 0 ? (
                      <div className="text-center py-12 text-text-secondary">
                        <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                        <p className="font-medium">{t('demo.noPortfolio')}</p>
                        <p className="text-sm mt-1">{t('demo.uploadWork')}</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {myPortfolio.map((item) => (
                          <div
                            key={item.id}
                            className="p-5 border border-gray-200 rounded-xl hover:border-primary-300 hover:shadow-soft transition-all group"
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-semibold bg-card-teal text-primary-600 mb-3">
                                  {item.category}
                                </span>
                                <h3 className="font-semibold text-text-primary group-hover:text-primary-600 transition-colors">{item.title}</h3>
                                <p className="text-sm text-text-secondary mt-1 line-clamp-2">{item.description}</p>
                              </div>
                            </div>
                            <p className="text-xs text-text-secondary mt-4">
                              {t('common.added')} {new Date(item.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Quick Actions & Achievements */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="card p-6">
                      <h2 className="text-lg font-bold text-text-primary mb-6 font-heading">{t('dashboard.quickActions')}</h2>
                      <div className="grid grid-cols-2 gap-4">
                        <Link
                          to="/student/portfolio/upload"
                          className="flex items-center p-4 border border-gray-200 rounded-xl hover:border-primary-300 hover:bg-card-teal transition-all group"
                        >
                          <div className="p-2 rounded-lg bg-card-teal text-primary-500 group-hover:bg-primary-100 transition-colors">
                            <Upload className="h-5 w-5" />
                          </div>
                          <span className="ml-3 text-sm font-semibold text-text-secondary">{t('common.upload')} {t('common.work')}</span>
                        </Link>
                        <Link
                          to="/student/progress"
                          className="flex items-center p-4 border border-gray-200 rounded-xl hover:border-primary-300 hover:bg-card-teal transition-all group"
                        >
                          <div className="p-2 rounded-lg bg-card-teal text-primary-500 group-hover:bg-primary-100 transition-colors">
                            <TrendingUp className="h-5 w-5" />
                          </div>
                          <span className="ml-3 text-sm font-semibold text-text-secondary">{t('common.view')} {t('dashboard.myProgress')}</span>
                        </Link>
                        <Link
                          to="/student/messages"
                          className="flex items-center p-4 border border-gray-200 rounded-xl hover:border-primary-300 hover:bg-card-teal transition-all group"
                        >
                          <div className="p-2 rounded-lg bg-card-teal text-primary-500 group-hover:bg-primary-100 transition-colors">
                            <MessageSquare className="h-5 w-5" />
                          </div>
                          <span className="ml-3 text-sm font-semibold text-text-secondary">{t('common.message')} {t('roles.teacher')}</span>
                        </Link>
                        <Link
                          to="/student/announcements"
                          className="flex items-center p-4 border border-gray-200 rounded-xl hover:border-primary-300 hover:bg-card-teal transition-all group"
                        >
                          <div className="p-2 rounded-lg bg-card-teal text-primary-500 group-hover:bg-primary-100 transition-colors">
                            <Bell className="h-5 w-5" />
                          </div>
                          <span className="ml-3 text-sm font-semibold text-text-secondary">{t('common.announcements')}</span>
                        </Link>
                      </div>
                    </div>

                    <div className="card p-6">
                      <h2 className="text-lg font-bold text-text-primary mb-6 font-heading">{t('demo.myAchievements')}</h2>
                      <div className="space-y-4">
                        {stats.totalPoints > 0 ? (
                          <>
                            <div className="flex items-center p-4 bg-card-green rounded-xl border border-success-200">
                              <div className="p-3 rounded-xl bg-success-100 text-success-500 mr-4">
                                <Award className="h-6 w-6" />
                              </div>
                              <div>
                                <p className="font-semibold text-success-600">{t('dashboard.points')} {t('common.earned')}</p>
                                <p className="text-sm text-text-secondary">{t('demo.greatWork')}</p>
                              </div>
                            </div>
                            {stats.totalPoints >= 100 && (
                              <div className="flex items-center p-4 bg-card-lavender rounded-xl border border-purple-200">
                                <div className="p-3 rounded-xl bg-purple-100 text-purple-500 mr-4">
                                  <Trophy className="h-6 w-6" />
                                </div>
                                <div>
                                  <p className="font-semibold text-purple-600">100+ {t('dashboard.points')} {t('demo.club')}</p>
                                  <p className="text-sm text-text-secondary">{t('demo.starPerformer')}</p>
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-center py-8 text-text-secondary">
                            <Star className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                            <p className="font-medium">{t('demo.noAchievements')}</p>
                            <p className="text-sm mt-1">{t('demo.earnPoints')}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              }
            />
            <Route path="*" element={<Navigate to="/student" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  )
}
